import { CircleAlert, Info, Pencil } from 'lucide-react'
import type { ReactNode, Ref } from 'react'

import { MarkdownEditorContainer } from '@/components/MarkdownEditorContainer'
import {
  MilkdownMarkdownEditor,
  milkdownModeProps,
} from '@/components/MilkdownMarkdownEditor'
import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'

import {
  CUSTOMIZE_LEAD,
  CUSTOMIZE_MESSAGE,
  MANAGED_ELSEWHERE_MESSAGE,
  OUT_OF_DATE_MESSAGE,
  VARIANT_FORK_MESSAGE,
} from '../sheetMessages'
import { CompareView, SEGMENT, SEGMENTED, type DiffMode } from './CompareView'
import type { CalloutId, CompareSpec, SheetModel } from './model'

const CALLOUT_TONE = {
  info: 'border-primary-subtle-border bg-primary-ui-bg text-primary-high-contrast',
  warning:
    'border-warning-ui-border bg-warning-ui-bg text-warning-low-contrast',
}

const CALLOUTS: Record<
  CalloutId,
  {
    tone: keyof typeof CALLOUT_TONE
    icon: ReactNode
    lead?: string
    text: string
  }
> = {
  customize: {
    tone: 'info',
    icon: <Pencil aria-hidden="true" />,
    lead: CUSTOMIZE_LEAD,
    text: CUSTOMIZE_MESSAGE,
  },
  outOfDate: {
    tone: 'warning',
    icon: <CircleAlert aria-hidden="true" />,
    text: OUT_OF_DATE_MESSAGE,
  },
  managedElsewhere: {
    tone: 'info',
    icon: <Info aria-hidden="true" />,
    text: MANAGED_ELSEWHERE_MESSAGE,
  },
  variantFork: {
    tone: 'info',
    icon: <Info aria-hidden="true" />,
    text: VARIANT_FORK_MESSAGE,
  },
}

/**
 * The editor, or, while comparing, the draft against the text it came from.
 * The editor stays mounted while hidden: Milkdown keeps its caret and undo
 * stack, and reads its body only at mount.
 */
export function ContentTab({
  model,
  editorKey,
  onContentChange,
  toggleRef,
  comparing,
  onCompareChange,
  base,
  content,
  onUseBase,
  mode,
  onModeChange,
}: {
  model: SheetModel | null
  /** Remounts the editor, which reads its body only at mount. */
  editorKey: number
  onContentChange: (content: string) => void
  toggleRef: Ref<HTMLDivElement>
  comparing: boolean
  onCompareChange: (comparing: boolean) => void
  base: { content: string | null; isLoading: boolean } | null
  content: string
  onUseBase: () => void
  mode: DiffMode
  onModeChange?: (mode: DiffMode) => void
}) {
  const compare = model?.compare ?? null
  const callout = model?.callout ? CALLOUTS[model.callout] : null
  return (
    <>
      {callout && (
        <Alert
          role="note"
          className={cn(
            'flex items-center gap-2.5 py-2',
            CALLOUT_TONE[callout.tone]
          )}>
          {callout.icon}
          <AlertDescription className="block flex-1 text-current">
            {callout.lead && (
              <span className="font-medium">{`${callout.lead} `}</span>
            )}
            {callout.text}
          </AlertDescription>
          {model?.callout === 'outOfDate' && !comparing && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => onCompareChange(true)}>
              Review changes
            </Button>
          )}
        </Alert>
      )}
      {(compare || model?.editor === 'preview') && (
        <div className="flex min-h-6 items-center justify-end text-xs font-medium text-muted-foreground">
          {compare ? (
            <ToggleGroup
              ref={toggleRef}
              type="single"
              aria-label="Content view"
              value={comparing ? 'compare' : 'edit'}
              onValueChange={(next) =>
                next && onCompareChange(next === 'compare')
              }
              className={SEGMENTED}>
              <ToggleGroupItem value="edit" className={SEGMENT}>
                Edit
              </ToggleGroupItem>
              <ToggleGroupItem
                value="compare"
                aria-label={compare.toggle}
                className={SEGMENT}>
                <span className="sm:hidden">Compare</span>
                <span className="hidden sm:inline">{compare.toggle}</span>
              </ToggleGroupItem>
            </ToggleGroup>
          ) : (
            'Read-only'
          )}
        </div>
      )}
      <div hidden={comparing}>
        <MarkdownEditorContainer className="min-h-40 rounded-lg shadow-[var(--shadow-md)] ring-neutral-ui-border [&_.milkdown]:rounded-lg! [&_.milkdown-top-bar]:rounded-t-lg!">
          <MilkdownMarkdownEditor
            key={editorKey}
            markdown={content}
            {...milkdownModeProps(model?.editor === 'preview', onContentChange)}
          />
        </MarkdownEditorContainer>
      </div>
      {comparing && compare && (
        <SheetSectionCard
          body={
            <CompareBody
              compare={compare}
              base={base}
              content={content}
              onUseBase={onUseBase}
              mode={mode}
              onModeChange={onModeChange}
            />
          }
        />
      )}
    </>
  )
}

function CompareBody({
  compare,
  base,
  content,
  onUseBase,
  mode,
  onModeChange,
}: {
  compare: CompareSpec
  base: { content: string | null; isLoading: boolean } | null
  content: string
  onUseBase: () => void
  mode: DiffMode
  onModeChange?: (mode: DiffMode) => void
}) {
  if (base?.content != null) {
    return (
      <CompareView
        from={compare.from}
        to={compare.to}
        oldValue={base.content}
        newValue={content}
        mode={mode}
        onModeChange={onModeChange}
        action={
          <Button
            type="button"
            variant="outline"
            size="xs"
            disabled={base.content === content}
            onClick={onUseBase}>
            {compare.useBase}
          </Button>
        }
      />
    )
  }
  if (base?.isLoading) return <Skeleton className="h-32 w-full" />
  return (
    <p className="text-sm text-muted-foreground">
      {`${compare.from}'s text is not available to compare.`}
    </p>
  )
}
